import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import ScrollReveal from "@/components/ScrollReveal";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Star } from "lucide-react";
import { useNavigate } from "react-router-dom";

const Results = () => {
  const navigate = useNavigate();

  return (
    <Layout>
      <SEOHead title="Real Results from AI Automation | Xentic Solutions" description="See how real businesses increased leads, bookings, and revenue with Xentic AI chatbot automation." path="/results" />

      <section className="py-20 md:py-28 relative overflow-hidden">
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-primary/15 rounded-full blur-[120px] animate-blob" />
        <div className="container text-center max-w-3xl relative z-10">
          <ScrollReveal>
            <p className="text-sm uppercase tracking-widest text-muted-foreground mb-3">Case Studies</p>
            <h1 className="font-heading text-4xl md:text-5xl font-bold">Real Businesses. Real Results. <span className="text-gradient">Real Numbers.</span></h1>
            <p className="mt-6 text-lg text-muted-foreground">Do not take our word for it, look at the data.</p>
          </ScrollReveal>
        </div>
      </section>

      <section className="py-12 relative overflow-hidden" style={{ background: "linear-gradient(135deg, hsl(189 100% 50%), hsl(243 76% 59%))" }}>
        <div className="container relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 text-center">
            {[
              { stat: "40-60%", label: "More Leads" },
              { stat: "<2s", label: "Response Time" },
              { stat: "80%", label: "Questions Automated" },
              { stat: "72hrs", label: "Average Setup" },
              { stat: "100%", label: "Satisfaction Rate" },
            ].map((s, i) => (
              <div key={i}>
                <div className="text-3xl font-heading font-bold text-white">{s.stat}</div>
                <div className="text-sm mt-1 text-white/70">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container">
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: "Marcus T.", biz: "HVAC Company Owner",
                quote: "We were missing over 60% of our after-hours leads. Customers would call, get voicemail, and call the next company. Since installing the Xentic chatbot, we capture almost every lead, even at 2am. The bot asks the right questions, gets their info, and books a service call. It paid for itself in the first 3 days.",
                result: "+58% lead capture", stars: 5,
              },
              {
                name: "Rachel K.", biz: "Dental Practice Manager",
                quote: "Our front desk was overwhelmed. Patients calling about hours, insurance, and appointment availability all day long. Now the chatbot handles 80% of those questions instantly. Our staff can focus on in-office patients, and online bookings have increased by 45%. I wish we'd done this a year ago.",
                result: "+45% online bookings", stars: 5,
              },
              {
                name: "David A.", biz: "Managing Attorney",
                quote: "Before Xentic, every inquiry got the same treatment, whether they were a serious client or just browsing. Now the chatbot qualifies leads by practice area, urgency, and budget before they ever reach us. We only spend time on people who are genuinely ready to hire. Our qualified lead rate jumped 62%.",
                result: "+62% qualified leads", stars: 5,
              },
            ].map((t, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <Card className="h-full">
                  <CardContent className="p-6">
                    <div className="flex gap-1 mb-3">
                      {Array.from({ length: t.stars }).map((_, j) => <Star key={j} className="h-4 w-4 fill-yellow-400 text-yellow-400" />)}
                    </div>
                    <p className="text-sm text-muted-foreground mb-4 italic">"{t.quote}"</p>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-heading font-bold text-sm">{t.name}</p>
                        <p className="text-xs text-muted-foreground">{t.biz}</p>
                      </div>
                      <span className="text-xs font-bold bg-primary/10 text-primary px-2 py-1 rounded-full border border-primary/20">{t.result}</span>
                    </div>
                  </CardContent>
                </Card>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 relative overflow-hidden" style={{ background: "linear-gradient(135deg, rgba(0,217,255,0.08), rgba(79,70,229,0.08))" }}>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[250px] bg-primary/10 rounded-full blur-[100px]" />
        <div className="container text-center relative z-10">
          <ScrollReveal>
            <h2 className="font-heading text-3xl font-bold mb-4">Want Results Like These?</h2>
            <p className="text-muted-foreground mb-8 max-w-lg mx-auto">Book a free consultation and see what AI automation can do for your business.</p>
            <Button
              size="lg"
              className="border-0 hover:-translate-y-0.5 transition-transform duration-300 glow-shadow-sm"
              style={{ background: "linear-gradient(135deg, hsl(189 100% 50%), hsl(243 76% 59%))" }}
              onClick={() => navigate("/contact")}
            >
              <span className="text-white font-semibold">Book Free Consultation →</span>
            </Button>
          </ScrollReveal>
        </div>
      </section>
    </Layout>
  );
};

export default Results;
